package dev.kotlinfest._2026.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.JapaneseFontFamily
import dev.kotlinfest._2026.LatinFontFamily
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.kotlinGradientBorder
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px

enum class SiteButtonType {
    PRIMARY,
    SECONDARY,
}

enum class SiteButtonSize {
    MEDIUM,
    LARGE,
}

enum class SiteButtonLanguage {
    EN,
    JP,
}

val PrimaryButtonStyle = CssStyle {
    base {
        Modifier.sitePrimaryButtonAppearance()
    }
    cssRule(":hover") {
        Modifier.siteButtonHover()
    }
    cssRule(":focus-within") {
        Modifier.siteButtonHover()
    }
    cssRule(":active") {
        Modifier.sitePrimaryButtonActive()
    }
}

val SecondaryButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(SitePalette.brand.softLime)
            .borderRadius(50.px)
            .color(SitePalette.brand.ink)
            .siteButtonTransition()
    }
    cssRule(":hover") {
        Modifier.siteButtonHover()
    }
    cssRule(":focus-within") {
        Modifier.siteButtonHover()
    }
    cssRule(":active") {
        Modifier.backgroundColor(SitePalette.brand.lime)
    }
}

val DisabledButtonStyle = CssStyle {
    base {
        Modifier.color(SitePalette.brand.muted).styleModifier {
            property("cursor", "default")
            property("filter", "grayscale(1)")
            property("opacity", "0.45")
            property("pointer-events", "none")
        }
    }
}

internal const val SiteButtonArrowPath = "/img/arrow.svg"
internal const val SiteButtonIconSizePx = 24

internal fun Modifier.siteButtonTransition(): Modifier = styleModifier {
    property("transition", "box-shadow 160ms ease, background-color 120ms ease")
}

internal fun Modifier.sitePrimaryButtonAppearance(): Modifier = kotlinGradientBorder()
    .backgroundColor(SitePalette.brand.white)
    .borderRadius(50.px)
    .color(SitePalette.brand.ink)
    .siteButtonTransition()

internal fun Modifier.siteButtonHover(): Modifier = boxShadow(0.px, 4.px, 6.px, color = SitePalette.brand.pink)

internal fun Modifier.sitePrimaryButtonActive(): Modifier = backgroundColor(SitePalette.brand.softPink)

internal fun Modifier.siteButtonSize(size: SiteButtonSize): Modifier = when (size) {
    SiteButtonSize.MEDIUM -> padding(top = 12.px, right = 22.px, bottom = 12.px, left = 24.px).gap(4.px)
    SiteButtonSize.LARGE -> padding(top = 24.px, right = 28.px, bottom = 24.px, left = 32.px).gap(8.px)
}

internal fun Modifier.siteButtonText(size: SiteButtonSize, language: SiteButtonLanguage): Modifier = fontFamily(if (language == SiteButtonLanguage.EN) LatinFontFamily else JapaneseFontFamily, "sans-serif")
    .fontWeight(600)
    .fontSize(if (size == SiteButtonSize.LARGE) 20.px else 17.px)
    .lineHeight(1.2)
    .letterSpacing(if (language == SiteButtonLanguage.EN) 0.1.em else 0.08.em)

@Composable
fun SiteButton(
    label: String,
    href: String? = null,
    modifier: Modifier = Modifier,
    type: SiteButtonType = SiteButtonType.PRIMARY,
    size: SiteButtonSize = SiteButtonSize.MEDIUM,
    language: SiteButtonLanguage = SiteButtonLanguage.JP,
    enabled: Boolean = href != null,
) {
    val isInteractive = enabled && href != null
    val style = if (type == SiteButtonType.PRIMARY) PrimaryButtonStyle else SecondaryButtonStyle
    val buttonModifier = style.toModifier()
        .then(
            if (isInteractive) {
                Modifier
            } else {
                DisabledButtonStyle.toModifier().role("button").ariaDisabled()
            },
        )
        .then(modifier)
        .siteButtonSize(size)
        .styleModifier { if (isInteractive) property("cursor", "pointer") }

    val content: @Composable () -> Unit = {
        Row(
            buttonModifier.styleModifier {
                if (type == SiteButtonType.PRIMARY) property("justify-content", "space-between")
            },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(label, Modifier.siteButtonText(size, language))
            if (type == SiteButtonType.PRIMARY) {
                Image(
                    SiteButtonArrowPath,
                    "",
                    Modifier.width(SiteButtonIconSizePx.px).height(SiteButtonIconSizePx.px),
                )
            }
        }
    }

    if (isInteractive) {
        Link(
            href,
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) {
            content()
        }
    } else {
        content()
    }
}

val TextButtonStyle = CssStyle {
    base {
        Modifier
            .color(SitePalette.brand.ink)
            .styleModifier { property("transition", "color 120ms ease") }
    }
    cssRule(":hover") {
        Modifier.color(SitePalette.brand.secondary)
    }
    cssRule(":focus-within") {
        Modifier.color(SitePalette.brand.secondary)
    }
}

@Composable
fun TextButton(
    label: String,
    href: String? = null,
    modifier: Modifier = Modifier,
    language: SiteButtonLanguage = SiteButtonLanguage.JP,
    enabled: Boolean = href != null,
) {
    val isInteractive = enabled && href != null
    val textModifier = TextButtonStyle.toModifier()
        .then(modifier)
        .fontFamily(if (language == SiteButtonLanguage.EN) LatinFontFamily else JapaneseFontFamily, "sans-serif")
        .fontWeight(600)
        .fontSize(17.px)
        .lineHeight(1.2)
        .letterSpacing(if (language == SiteButtonLanguage.EN) 0.1.em else 0.08.em)
        .then(if (isInteractive) Modifier else Modifier.role("link").ariaDisabled())
        .styleModifier {
            property("cursor", if (isInteractive) "pointer" else "default")
            property("opacity", "1")
            if (!isInteractive) property("pointer-events", "none")
        }

    if (isInteractive) {
        Link(
            href,
            label,
            textModifier,
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        )
    } else {
        SpanText(label, textModifier)
    }
}
